import type React from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ListView } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import {
    FOCUS_CART_SHORTCUT,
    renderWithKeyShortcuts,
} from '@/components/shortcuts';
import { PaymentModal } from '../../payment/PaymentModal';
import { DiscountDialog } from '../discountDialog';
import { CartFooter } from './CartFooter';
import { CartHeader } from './CartHeader';
import { CartItemRow } from './CartItemRow';
import type { CartPanelState } from './useCartPanel';
import { VoidConfirmDialog } from './VoidConfirmDialog';

const renderCartList = renderWithKeyShortcuts(
    'div',
    FOCUS_CART_SHORTCUT.combination,
);

type CartPanelViewProps = CartPanelState & {
    /** HOLD's press: a "coming soon" notice until its flow lands. */
    onHold: () => void;
};

/**
 * The cart pane: header, lines (or the empty state), totals and the action
 * row, plus the payment modal, the void confirm and the discount dialog it
 * opens. Each line has a discount button and the totals a Sale discount row,
 * opening the same dialog as `F6`/`F7` (SEL-100).
 *
 * The lines are a `ListView` grid list (bos-seller#56, the spike result on
 * bos-seller#47): one Tab stop, `↑`/`↓` move between lines, `→`/`←` walk
 * into and out of the stepper and remove button. `listProps` from
 * `useCartKeyboardNav` go on the list's wrapper and layer what the design
 * system doesn't give for free — row-level `+`/`-`/`Delete`, letters and
 * digits sent to search, focus after a line leaves. The list names `F4`, the
 * key that focuses its last line, with `aria-keyshortcuts`.
 */
export const CartPanelView: React.FC<CartPanelViewProps> = ({
    cart,
    lines,
    cartCount,
    subtotal,
    lineDiscountTotal,
    saleDiscount,
    saleDiscountAmount,
    isSaleDiscountInvalid,
    isLineDiscountInvalid,
    total,
    discountNotice,
    isEmpty,
    canPay,
    registerItemRef,
    listProps,
    onLineQnt,
    onRemoveLine,
    onLineDiscount,
    onSaleDiscount,
    onHold,
    payment,
    voidConfirm,
    discount,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex w-[380px] flex-shrink-0 flex-col border-l border-border bg-surface"
            {...getNodeTestIdProp('seller-cart')}
        >
            <CartHeader
                cartCount={cartCount}
                isEmpty={isEmpty}
                onClear={voidConfirm.open}
            />

            <div className="flex-1 overflow-y-auto">
                {isEmpty ? (
                    <div
                        className="flex h-full items-center justify-center"
                        {...getNodeTestIdProp('seller-cart-empty')}
                    >
                        <EmptyState>
                            <EmptyState.Header>
                                <EmptyState.Media variant="icon">
                                    <Icon icon="solar:cart-cross-bold-duotone" />
                                </EmptyState.Media>
                                <EmptyState.Title>
                                    {t('seller.cartEmpty')}
                                </EmptyState.Title>
                                <EmptyState.Description>
                                    {t('seller.cartEmptyHint')}
                                </EmptyState.Description>
                            </EmptyState.Header>
                        </EmptyState>
                    </div>
                ) : (
                    <div
                        className="px-5"
                        {...listProps}
                        {...getNodeTestIdProp('seller-cart-items')}
                    >
                        <ListView
                            aria-label={t('seller.cart')}
                            render={renderCartList}
                            selectionMode="none"
                            variant="secondary"
                        >
                            {lines.map((view) => {
                                const id = view.line.variant.variantId;
                                return (
                                    <CartItemRow
                                        itemRef={registerItemRef(id)}
                                        key={id}
                                        onDiscount={() => onLineDiscount(id)}
                                        onQnt={(qnt) => onLineQnt(id, qnt)}
                                        onRemove={() => onRemoveLine(id)}
                                        view={view}
                                    />
                                );
                            })}
                        </ListView>
                    </div>
                )}
            </div>

            <CartFooter
                canPay={canPay}
                discountNotice={discountNotice}
                isEmpty={isEmpty}
                isLineDiscountInvalid={isLineDiscountInvalid}
                isSaleDiscountInvalid={isSaleDiscountInvalid}
                lineDiscountTotal={lineDiscountTotal}
                onSaleDiscount={onSaleDiscount}
                saleDiscount={saleDiscount}
                saleDiscountAmount={saleDiscountAmount}
                onHold={onHold}
                onPay={payment.openModal}
                onVoid={voidConfirm.open}
                subtotal={subtotal}
                total={total}
            />

            <PaymentModal {...payment} />
            <DiscountDialog {...discount} />
            <VoidConfirmDialog
                {...voidConfirm}
                lineCount={cart.length}
                total={total}
            />
        </div>
    );
};
